import { createCacheKey, ENDPOINTS, fetchResource, useCreatePayload } from 'hammock';
import React, { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { s3Image } from 'site-constants';
import styled from 'styled-components';
import { H2, Input, P } from 'styleguide';

import { ImageContainer } from './ImageContainer';
import { LoadChunks } from './LoadChunks';

const Container = styled.div`
  align-self: stretch;
  display: flex;
  flex-direction: column;
  flex: 1;
  margin: 0;
  padding: 30px 30px 10px;
`;

const ImagesSection = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-top: 10px;
  max-height: calc(100vh - 23em);
  overflow: auto;
`;

const EmptyState = styled.div`
  align-items: center;
  flex: 1;
  flex-direction: column;
  display: flex;
  justify-content: center;
`;

export interface Image {
  fileName: string;
  assetUri: { uri: string };
  modifierAt: string;
  mediaId: number;
}

interface Props {
  isAiMode: boolean;
  selectedImages: string[];
  setSelectedImages: (images: string[]) => void;
  toggleModal: () => void;
  onSelectImage: (url: string, dimensions?: { width: number; height: number }) => void;
}

export const MediaContent = ({
  toggleModal,
  onSelectImage,
  isAiMode,
  selectedImages,
  setSelectedImages,
}: Props) => {
  const [filter, setFilter] = useState('');
  const [deletedImages, setDeletedImages] = useState(new Set());

  const { getAll } = useCreatePayload(ENDPOINTS.images);

  const imagesPayload = getAll({ query: { finder: 'byOrganizationId' } });

  const { data: images } = useQuery<Image[]>(
    createCacheKey(imagesPayload),
    () => fetchResource(imagesPayload),
    {
      staleTime: 0,
    },
  );

  const filteredImages = useMemo(
    () =>
      images?.filter(
        (image) =>
          image.fileName.toLowerCase().includes(filter.toLowerCase()) &&
          !deletedImages.has(image.mediaId),
      ) || [],
    [filter, images, deletedImages],
  );

  const [ImageContainerNode, setImageContainerNode] = useState(null);
  const onRefChange = useCallback((n: any) => setImageContainerNode(n), []);

  const onRemoveImage = (id: number) => setDeletedImages(deletedImages.add(id));

  return (
    <Container>
      <H2>Media Library</H2>
      <Input
        placeholder="Search media"
        onChange={({ target: { value } }: ChangeEvent<HTMLInputElement>) => setFilter(value)}
        css={{ width: 'initial' }}
      />
      {(!images || !images?.length) && (
        <EmptyState>
          <img
            src={s3Image('/png/media-library-empty-placeholder.png')}
            alt="No files yet added."
          />
          <P>No files yet added.</P>
        </EmptyState>
      )}
      <ImagesSection ref={onRefChange}>
        <LoadChunks
          limit={20}
          list={filteredImages}
          render={(item) =>
            ImageContainerNode &&
            item?.map((image) => (
              <ImageContainer
                key={image.assetUri.uri}
                image={image}
                isAiMode={isAiMode}
                selectedImages={selectedImages}
                changeSelectedImages={setSelectedImages}
                parentRef={ImageContainerNode}
                toggleModal={toggleModal}
                onSelectImage={onSelectImage}
                onRemoveImage={onRemoveImage}
              />
            ))
          }
        />
      </ImagesSection>
    </Container>
  );
};
